/**
 * usePersistedState — Hook utilitario para conservar filtros / preferencias
 * entre navegaciones de SPA. Guarda en localStorage bajo `ascauto:filters:{key}`.
 *
 * Uso:
 *   const [view, setView] = usePersistedState("appointments.activeView", "calendar");
 *
 * Notas:
 *  - Serializa JSON. NO usar para Blobs/Funciones/etc.
 *  - Si el JSON guardado es inválido o de un tipo distinto, vuelve al default.
 *  - El namespace `ascauto:filters:` evita colisiones con otros usos de LS.
 *  - Se sincroniza entre pestañas vía evento `storage`.
 */
import { useState, useEffect, useCallback } from "react";

const NS = "ascauto:filters:";

export default function usePersistedState(key, defaultValue) {
  const storageKey = `${NS}${key}`;

  const read = useCallback(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === null) return defaultValue;
      return JSON.parse(raw);
    } catch {
      return defaultValue;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const [value, setValue] = useState(read);

  // Persistir al cambiar
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      /* quota / privacidad: silencioso */
    }
  }, [storageKey, value]);

  // Sincronizar entre pestañas
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== storageKey) return;
      try {
        const next = e.newValue === null ? defaultValue : JSON.parse(e.newValue);
        setValue(next);
      } catch { /* ignore */ }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  return [value, setValue];
}

/** Borra TODAS las claves del namespace `ascauto:filters:*`. */
export function clearAllPersistedFilters() {
  try {
    const toRemove = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(NS)) toRemove.push(k);
    }
    toRemove.forEach(k => window.localStorage.removeItem(k));
  } catch { /* ignore */ }
}

/** Borra claves filtradas por prefijo dentro del namespace. */
export function clearPersistedFiltersByPrefix(prefix) {
  try {
    const fullPrefix = `${NS}${prefix}`;
    const toRemove = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(fullPrefix)) toRemove.push(k);
    }
    toRemove.forEach(k => window.localStorage.removeItem(k));
  } catch { /* ignore */ }
}
